import moment from "moment";
import { Area, ScheduleStatus, UnitOfMeasure, UnitRow, UnitStatus } from "./types";

export const number = (value: number | null | undefined) =>
	value === null || value === undefined ? "–" : Math.round(value).toLocaleString("en-US");

export const percent = (part: number, whole: number, digits = 2) =>
	whole > 0 ? `${((part / whole) * 100).toFixed(digits)}%` : "–";

export const ratio = (part: number, whole: number) => (whole > 0 ? part / whole : 0);

export const ZERO_AREA: Area = { sqft: 0, sqm: 0 };

export const LEAD_UNIT: UnitOfMeasure = "sqm";

export const otherUnit = (unit: UnitOfMeasure): UnitOfMeasure => (unit === "sqm" ? "sqft" : "sqm");

export const area = (value: Area | null | undefined, unit: UnitOfMeasure = LEAD_UNIT) => `${number(value ? value[unit] : 0)} ${unit}`;

export const bothAreas = (value: Area | null | undefined, unit: UnitOfMeasure = LEAD_UNIT) =>
	`${area(value, unit)} · ${area(value, otherUnit(unit))}`;

export const addAreas = (values: Area[]): Area =>
	values.reduce((sum, value) => ({ sqft: sum.sqft + value.sqft, sqm: sum.sqm + value.sqm }), ZERO_AREA);

export const subtractArea = (left: Area, right: Area): Area => ({ sqft: left.sqft - right.sqft, sqm: left.sqm - right.sqm });

export const areaRatio = (part: Area, whole: Area) => (whole.sqft > 0 ? part.sqft / whole.sqft : whole.sqm > 0 ? part.sqm / whole.sqm : 0);

export const areaPercent = (part: Area, whole: Area, digits = 2) =>
	whole.sqft > 0 || whole.sqm > 0 ? `${(areaRatio(part, whole) * 100).toFixed(digits)}%` : "–";

export const date = (value: string | null | undefined, pattern = "DD MMM YYYY") =>
	value ? moment(value).format(pattern) : "–";

export const shortDate = (value: string | null | undefined) => date(value, "DD MMM");

export const daysFromToday = (value: string | null | undefined, today: string) => {
	if (!value) return null;
	return moment(value).startOf("day").diff(moment(today).startOf("day"), "days");
};

export const relativeDays = (value: string | null | undefined, today: string) => {
	const days = daysFromToday(value, today);
	if (days === null) return "";
	if (days === 0) return "today";
	return days > 0 ? `in ${days}d` : `${-days}d ago`;
};

export const signedDays = (value: number | null | undefined) => {
	if (value === null || value === undefined) return "–";
	return value > 0 ? `+${value}d` : `${value}d`;
};

export const plural = (count: number, one: string, many = `${one}s`) => `${number(count)} ${count === 1 ? one : many}`;

export const TRADING_RULE =
	"Leased and open: on or after the Date Tenant Opened for Trade on the Opening Certificate task, or the unit has no opening project (existing tenants, renewals). The agreed opening date and closing the project are never used.";

export const scheduleStatus = (unit: UnitRow): ScheduleStatus =>
	unit.upcoming && (unit.project_pending || !unit.project_id) ? "upcoming" : unit.status;

export const SCHEDULE_LABELS: { [key in ScheduleStatus]: string } = {
	trading: "Trading",
	design: "In Design",
	fitout: "In Fit-out",
	vacant: "Vacant",
	upcoming: "Upcoming"
};

export const NEXT_STEP: { [key in UnitStatus]: string } = {
	trading: "Stays Trading until the lease ends or the unit is vacated.",
	design: "Next: moves to Fit-out on the Actual Move in Date recorded on the Site Handover task.",
	fitout: "Next: moves to Trading on the Date Tenant Opened for Trade recorded on the Opening Certificate task.",
	vacant: "Next: moves to Design when a new lease is signed and its opening project starts."
};
